import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { ExternalApplication } from "../../../model/external-application";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { PortalDAO } from "../../../daos/portal.dao";
import { FormControl } from "@angular/forms";
import { TranslocoService } from '@ngneat/transloco';
import tinycolor from "tinycolor2";

@Component({
  selector: 'app-external-applications-form',
  templateUrl: './external-applications-form.component.html',
  styleUrls: ['./external-applications-form.component.scss']
})
export class ExternalApplicationsFormComponent implements OnInit {

  maxFileSizeKB: number = 250;

  externalApplication: ExternalApplication;
  externalApplicationForm: FormGroup;
  persistingChanges: any;
  maxSizeErrorMessage: string;
  successEnd: boolean = false;

  public uploadedFiles: any[] = [];
  public image: File = null;
  public imageHex: string;

  @ViewChild('fileUploader', {static: false})
  fileInput: ElementRef;

  mode: 'create' | 'edit';

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private messageService: MessageService,
              private portalDAO: PortalDAO,
              private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      let firstPath = urlSegment[0].path;
      if(firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  private loadCreationForm(): void {
    this.externalApplication = new ExternalApplication();
    this.externalApplication.color = "#2196F3";
    this.createForm(this.externalApplication);
  }

  private createForm(externalApplication: ExternalApplication): void {
    this.externalApplicationForm = this.formBuilder.group({
      'name': [externalApplication.name, [Validators.required, Validators.maxLength(255)]],
      'description': [externalApplication.description, [Validators.maxLength(255)]],
      'url': [externalApplication.url, [Validators.required, Validators.maxLength(255)]],
      'color': [externalApplication.color, [Validators.required]]});
  }

  loadEditionForm(uuid) {
    this.portalDAO.getExternalApplication(uuid).then((response) => {
      this.externalApplication = response as unknown as ExternalApplication;
      this.createForm(this.externalApplication);
      this.externalApplicationForm.addControl('uuid', new FormControl(this.externalApplication.uuid, [Validators.required, Validators.maxLength(36)]));
      this.imageHex = 'data:image/png;base64,' + this.externalApplication.icon;
      this.image = this.base64ToFile(this.externalApplication.icon);
    });
  }

  public getIcon(): string {
    return this.imageHex;
  }

  public dispatchClick() {
    this.fileInput.nativeElement.click();
  }

  public fileChangeEvent(filesInput: any) {
    if (filesInput.target.files[0]) {
      if (filesInput.target.files[0].size <= this.maxFileSizeKB * 1024) {
        this.maxSizeErrorMessage = "";
        this.image = filesInput.target.files[0];
        this.fileToBase64(this.image).then((response) => {
          this.imageHex = response.toLocaleString();
        });
      } else {
        this.maxSizeErrorMessage = this.maxFileSizeKB + " KB.";
      }
    }
  }

  private base64ToFile(imageHex: string): File {
    // convert base64 to Blob
    const byteCharacters = atob(imageHex);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {type: 'image/png'});
    return <File>blob; // cast to File
  }

  private fileToBase64(imageToConvert: File) {
    return (new Promise ((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(imageToConvert);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
      })
    );
  }

  public removeImage() {
    this.image = null;
    this.fileInput.nativeElement.value = null;
  }

  public isValidFile(): boolean {
    return this.image !== null;
  }

  persistAssetData() {
    if(!this.externalApplicationForm.valid || !this.isValidFile()) { return; }

    this.persistingChanges = true;
    this.save();
  }

  public save() {
    const formData: FormData = new FormData();
    formData.append('uploadingFiles', this.image);
    formData.append('applicationData', JSON.stringify(this.externalApplicationForm.getRawValue()));

    let promise;
    if (this.mode == 'create') {
      promise = this.portalDAO.createExternalApplication(formData);
    } else {
      promise = this.portalDAO.editExternalApplication(formData, this.externalApplication.uuid);
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.messageService.add({severity: 'success',
                                 summary: this.translocoService.translate('forms.externalApplication.success.create.summary'),
                                 detail: this.translocoService.translate('forms.externalApplication.success.create.detail'),
                                 sticky: false});
      } else {
        this.messageService.add({severity: 'success',
                                 summary: this.translocoService.translate('forms.externalApplication.success.edit.summary'),
                                 detail: this.translocoService.translate('forms.externalApplication.success.edit.detail'),
                                 sticky: false});
      }
      this.successEnd = true;
      this.router.navigate([`/`]);
    }).catch(error => {
      this.messageService.add({severity: 'error',
                               summary: this.translocoService.translate('forms.externalApplication.error.summary'),
                               detail: this.translocoService.translate('forms.externalApplication.error.detail'),
                               sticky: true});
      this.persistingChanges = false;
    });
  }

}
